import React, { useState } from "react";
import { Check, ChevronRight, RotateCcw } from "lucide-react";
import type { WorkbenchFix } from "@/types/workbench";
import { fixLifecycle, formatShortDate, type QueueGroup } from "@/pages/workbench/workbenchUtil";

type Props = {
  groups: QueueGroup[];
  totalCount: number;
  activeKey: number | null;
  reportDate?: string | null;
  onSelect: (signalKey: number) => void;
};

const SEV_LABEL: Record<string, string> = { critical: "Critical", issue: "Issue" };

/** The left-hand fix queue: grouped, re-ranks on completion, severity as the
 *  load-bearing colour and tier as a quiet badge. Completed fixes live in a
 *  trailing, collapsible "Done" group so the active view stays focused. */
export function FixQueue({ groups, totalCount, activeKey, reportDate, onSelect }: Props) {
  const openTotal = groups
    .filter((g) => g.key !== "done")
    .reduce((n, g) => n + g.fixes.length, 0);
  // Completed hidden by default while there's open work; auto-open if all done.
  const [showDone, setShowDone] = useState(() => openTotal === 0);

  return (
    <aside className="wb-queue" aria-label="Fix queue">
      <div className="wb-queue__hd">
        <h2>Your fix queue</h2>
        <span className="wb-queue__ct tnum">{totalCount}</span>
      </div>
      <div className="wb-queue__scroll">
        {groups.map((group) => {
          const isDone = group.key === "done";
          if (isDone) {
            return (
              <div key={group.key}>
                <button
                  type="button"
                  className="wb-q-sep wb-q-sep--toggle"
                  aria-expanded={showDone}
                  onClick={() => setShowDone((v) => !v)}
                >
                  <ChevronRight
                    size={13}
                    className="wb-q-sep__chev"
                    style={{ transform: showDone ? "rotate(90deg)" : "none" }}
                    aria-hidden="true"
                  />
                  {showDone ? "Hide completed" : "Show completed"}
                  <span className="wb-q-sep__ct tnum">{group.fixes.length}</span>
                </button>
                {showDone &&
                  group.fixes.map((fix) => (
                    <QueueRow key={fix.signal_key} fix={fix} active={fix.signal_key === activeKey} reportDate={reportDate} onSelect={onSelect} />
                  ))}
              </div>
            );
          }
          return (
            <div key={group.key}>
              <div className="wb-q-sep">
                {group.label}
                <span className="wb-q-sep__ct tnum">{group.fixes.length}</span>
              </div>
              {group.fixes.map((fix) => (
                <QueueRow key={fix.signal_key} fix={fix} active={fix.signal_key === activeKey} reportDate={reportDate} onSelect={onSelect} />
              ))}
            </div>
          );
        })}
      </div>
    </aside>
  );
}

function QueueRow({
  fix,
  active,
  reportDate,
  onSelect,
}: {
  fix: WorkbenchFix;
  active: boolean;
  reportDate?: string | null;
  onSelect: (signalKey: number) => void;
}) {
  const lifecycle = fixLifecycle(fix, reportDate);
  const done = lifecycle === "done";
  const regressed = lifecycle === "regressed";
  const completed = formatShortDate(fix.task.completed_at);

  // A regressed fix is active work again, so it keeps the severity look (bar +
  // dot); only a genuinely-done fix gets the muted "done" row + check.
  const cls = [
    "wb-q-item",
    done ? "wb-q-item--done" : `wb-q-item--${fix.severity}`,
    active ? "wb-q-item--active" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const chipCls = done ? "done" : regressed ? "recheck" : fix.severity;
  const chipLabel = done ? "Done" : regressed ? "Rechecked" : SEV_LABEL[fix.severity] ?? fix.severity;

  return (
    <button
      type="button"
      className={cls}
      onClick={() => onSelect(fix.signal_key)}
      aria-current={active ? "true" : undefined}
    >
      <span className="wb-q-item__dot" aria-hidden="true">
        {done && <Check size={11} strokeWidth={3} />}
      </span>
      <span className="wb-q-item__body">
        <span className="wb-q-item__nm">{fix.title}</span>
        <span className="wb-q-item__meta">
          <span className="wb-q-item__tech">{fix.tech}</span>
          <span className="wb-tier">{fix.tier_label}</span>
          {done && completed && <span className="wb-q-item__when">Done {completed}</span>}
          {regressed && completed && (
            <span className="wb-q-item__when wb-q-item__when--stale">Marked done {completed}</span>
          )}
        </span>
      </span>
      <span className={`wb-sev wb-sev--${chipCls}`}>
        {regressed && <RotateCcw size={10} strokeWidth={2.5} aria-hidden="true" />}
        {chipLabel}
      </span>
    </button>
  );
}

export default FixQueue;
